//#region IMPORTS
import {
  CANCEL_ORDER_SURE,
  SHOW_SUBORDER_INFO,
  CHOOSE_PICKLIST_SUPPLIER,
  CONFIRM_PICKLIST_DOWNLOAD,
  BULK_CONFIRM_THRESHOLD,
  UPDATE_SUBORDER_TRACKINGFORM_MODAL,
} from "../../models/_consts";
import {
  fetchFilters,
  ordersSelectors,
  fetchOrdersAsync,
  updateOrderAsync,
  setSelectedOrders,
  setSelectedSubOrder,
  fetchApprovableOrdersAsync,
  fetchShippableOrdersAsync,
  fetchInProgressOrdersAsync,
  setBulkRefreshing,
} from "../../features/orders/ordersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, ReactNode, useEffect, useState } from "react";
import { ORDERSTATUS, ORDERTYPE, Order } from "../../models/order";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { openModal } from "../../features/dashBoard/dashboardSlice";
import { toast } from "react-toastify";
import OrdersTableControlePanel from "./OrdersTableControlePanel";
import SubOrderTrackingForm from "../suborderTracking/SubOrderTrackingForm";
import OrdersFilterBar from "./OrdersFilterBar";
import SupplierSelect from "../modalTypes/SupplierSelect";
import SubOrderDetail from "../suborderDetail/SubOrderDetail";
import MainContainer from "../../components/_layout/MainContainer";
import OrdersTable from "../../components/orders/OrdersTable";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import AreYouSure from "../modalTypes/AreYouSure";
import MainModal from "../_layout/MainModal";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  hideOrderstatusFilter?: boolean;
  hideOrdertypeFilter?: boolean;
  clientPaginate?: boolean;
  headerExtra?: ReactNode;
  title?: string;
}
//#endregion

export default function OrdersContainer({
  hideOrderstatusFilter = false,
  hideOrdertypeFilter = false,
  clientPaginate = false,
  headerExtra,
  title = "rituals.orderHistory",
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router — the active flow preset (Approve / Manage / Send / ...)
  const { search } = useLocation();
  const preset = new URLSearchParams(search).get("preset");

  // Redux
  const dispatch = useAppDispatch();
  const orders = useAppSelector(ordersSelectors.selectAll);
  const {
    selectedSubOrder,
    filters_loaded,
    selectedOrders,
    orders_loaded,
    selectedOrder,
    metaData,
    filters,
    loading,
  } = useAppSelector((state) => state.orders);

  // Local state
  const [orderToCancel, setOrderToCancel] = useState<Order | null>(null);
  const [pendingSupplier, setPendingSupplier] = useState<string | undefined>(
    undefined,
  );
  // Single-row picklist download (kept separate from the multi-selection so it
  // does not clear the user's current selection).
  const [picklistOrder, setPicklistOrder] = useState<Order | null>(null);
  //#endregion

  //#region LOGIC
  // Load filters
  useEffect(() => {
    const fetchFiltering = async () => {
      await dispatch(fetchFilters());
    };
    !filters_loaded && fetchFiltering();
  }, [filters_loaded, dispatch]);

  // Check for warning when changing orderstatus to cancelled
  const checkIfWarningIsNeeded = async (order: Order, order_status: string) => {
    if (order_status === ORDERSTATUS.cancelled) {
      setOrderToCancel(order);
      dispatch(openModal(CANCEL_ORDER_SURE));
    } else {
      changeOrderStatusHandler(order, order_status);
    }
  };

  // Change orderstatus shortcut handler
  const changeOrderStatusHandler = async (
    order: Order,
    order_status: string,
  ) => {
    // Lock the page (and the auto-advance) while the status change and the
    // count refetches are in flight — otherwise the next-pill counts are still
    // stale and PromoOrdersView falls through to the Backorder overview.
    dispatch(setBulkRefreshing(true));
    try {
      await dispatch(
        updateOrderAsync({
          orderId: order.id,
          values: {
            buyer_id: order.buyer_id,
            comment: order.comment,
            order_code: order.order_code,
            promo_id: order.promo_id,
            order_status,
            order_type: order.order_type,
          },
        }),
      );

      // Change status of suborders
      const updateValues = {
        sub_orders: order.sub_orders.map((subOrder) => subOrder.id.toString()),
        order_status,
      };

      // Check if suborder can be changed
      if (
        order.sub_orders.some(
          (subOrder) => subOrder.order_status === ORDERSTATUS.shipped,
        )
      ) {
        toast.warning("Suborders that are shipped can not be changed anymore");
      } else {
        await agent.Orders.updateMultiSubOrderStatus(updateValues);
      }

      // Show message
      toast.success(
        `Order ${order.order_code} is now ${order_status.toLowerCase()}`,
      );

      // Keep the flow counts fresh (badges + auto-advance) after a per-row
      // change — await them so the bulkRefreshing flag drops only once all
      // badges have settled.
      await Promise.all([
        dispatch(fetchOrdersAsync()),
        dispatch(fetchApprovableOrdersAsync()),
        dispatch(fetchShippableOrdersAsync()),
        dispatch(fetchInProgressOrdersAsync()),
      ]);
    } catch (error) {
    } finally {
      dispatch(setBulkRefreshing(false));
    }
    cancelChangeOrder();
  };

  // Cancel changing an order
  const cancelChangeOrder = () => {
    setOrderToCancel(null);
    setPicklistOrder(null);
    dispatch(openModal(false));
  };

  // Cancel changing an order
  const cancelSubOrderActions = (refresh?: boolean) => {
    refresh && dispatch(fetchOrdersAsync());
    dispatch(openModal(false));
    dispatch(setSelectedSubOrder({ order: null, subOrder: null }));
  };

  // Manage flow: requesting the lists also advances the same orders (batch or
  // single) from "Ordered" to "In progress". Only orders that are actually in
  // the ordered backorder state are touched.
  const advanceOrdersToInProgress = async (orders: Order[]) => {
    const eligible = orders.filter(
      (order) =>
        order.order_status === ORDERSTATUS.ordered &&
        order.order_type !== ORDERTYPE.original,
    );
    if (eligible.length === 0) return;
    try {
      await agent.Orders.updateMultiOrderStatus({
        orders: eligible.map((order) => order.id.toString()),
        order_status: ORDERSTATUS.inProgress,
      });
      const subOrders = eligible.flatMap((order) => order.sub_orders);
      if (subOrders.some((sub) => sub.order_status === ORDERSTATUS.shipped)) {
        toast.warning("Suborders that are shipped can not be changed anymore");
      } else if (subOrders.length > 0) {
        await agent.Orders.updateMultiSubOrderStatus({
          sub_orders: subOrders.map((sub) => sub.id.toString()),
          order_status: ORDERSTATUS.inProgress,
        });
      }
      toast.success(
        `${eligible.length} order${
          eligible.length > 1 ? "s" : ""
        } set in progress`,
      );
    } catch (error: any) {
      toast.error(error?.message ?? `${error}`);
    }
  };

  // Request picklists for a set of orders (shared by bulk + single-row).
  const generateListsAndEmailHandler = async (
    orders: Order[],
    supplier: string | undefined,
    clearSelection: boolean,
  ) => {
    let downloaded = false;
    try {
      const { message } = await agent.Reports.emailMeListsForSelectedOrders({
        orders: orders.map((order) => order.id),
        sub_orders: true,
        supplier,
      });
      toast.success(message, { autoClose: 2000 });
      downloaded = true;
    } catch (error) {
      toast.error(`${error}`);
    }

    // In the Manage preset a successful download doubles as the "set in
    // progress" step for the same orders.
    if (downloaded && preset === "handle")
      await advanceOrdersToInProgress(orders);

    dispatch(fetchOrdersAsync());
    dispatch(fetchApprovableOrdersAsync());
    dispatch(fetchShippableOrdersAsync());
    dispatch(fetchInProgressOrdersAsync());
    if (clearSelection) dispatch(setSelectedOrders([]));
    setPicklistOrder(null);
    dispatch(openModal(false));
  };

  // Request lists in bulk (current selection)
  const bulkGenerateListsAndEmailHandler = (supplier?: string) =>
    generateListsAndEmailHandler(selectedOrders, supplier, true);

  // Open the supplier picker for a single order's picklist
  const requestPicklistForOrder = (order: Order) => {
    setPicklistOrder(order);
    dispatch(openModal(CHOOSE_PICKLIST_SUPPLIER));
  };

  const resetHandler = async () => {
    await dispatch(fetchOrdersAsync());
    await dispatch(fetchFilters());
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      {/* MODALS */}
      <MainModal type={CANCEL_ORDER_SURE} closeHandler={cancelChangeOrder}>
        <AreYouSure
          closeHandler={cancelChangeOrder}
          confirmHandler={() =>
            orderToCancel &&
            changeOrderStatusHandler(orderToCancel, "Cancelled")
          }
          title="Cancel order?"
          message={`Are you sure you want to cancel order ${orderToCancel?.order_code}`}
        />
      </MainModal>

      <MainModal
        type={CHOOSE_PICKLIST_SUPPLIER}
        closeHandler={cancelChangeOrder}
      >
        <SupplierSelect
          stock_portal={filters.stock_portal}
          closeHandler={cancelChangeOrder}
          confirmHandler={(supplier: string) => {
            if (picklistOrder) {
              // Single-row download: always immediate (one order).
              generateListsAndEmailHandler([picklistOrder], supplier, false);
            } else if (selectedOrders.length > BULK_CONFIRM_THRESHOLD) {
              setPendingSupplier(supplier);
              dispatch(openModal(CONFIRM_PICKLIST_DOWNLOAD));
            } else {
              bulkGenerateListsAndEmailHandler(supplier);
            }
          }}
          title="Choose Supplier?"
          message="Select the suppliers to generate picklists"
        />
      </MainModal>

      <MainModal
        type={CONFIRM_PICKLIST_DOWNLOAD}
        closeHandler={cancelChangeOrder}
      >
        <AreYouSure
          closeHandler={cancelChangeOrder}
          confirmHandler={() => bulkGenerateListsAndEmailHandler(pendingSupplier)}
          title="Request lists"
          message={`Request lists for ${selectedOrders.length} order${
            selectedOrders.length === 1 ? "" : "s"
          }?`}
        />
      </MainModal>

      <MainModal
        type={SHOW_SUBORDER_INFO}
        closeHandler={cancelSubOrderActions}
        extraTopMargin={selectedOrder && selectedOrder?.order_items.length > 10}
      >
        <SubOrderDetail order={selectedOrder} subOrder={selectedSubOrder} />
      </MainModal>

      {selectedSubOrder && (
        <MainModal
          type={UPDATE_SUBORDER_TRACKINGFORM_MODAL}
          closeHandler={cancelSubOrderActions}
        >
          <SubOrderTrackingForm
            subOrder={selectedSubOrder}
            closeHandler={() => cancelSubOrderActions(true)}
          />
        </MainModal>
      )}

      {/* BODY */}
      <MainContainer>
        <PageHeader
          title={t(title)}
          disabled={loading}
          reset={resetHandler}
          extra={headerExtra}
        />

        <OrdersFilterBar
          hideOrderstatusFilter={hideOrderstatusFilter}
          hideOrdertypeFilter={hideOrdertypeFilter}
          extensionButtons={<OrdersTableControlePanel />}
        />

        <OrdersTable
          changeOrderStatusHandler={(order: Order, status: string) =>
            checkIfWarningIsNeeded(order, status)
          }
          downloadPicklistHandler={requestPicklistForOrder}
          itemsLoaded={orders_loaded}
          clientPaginate={clientPaginate}
          metaData={metaData}
          items={orders}
          hide={orders.length === 0}
        />
      </MainContainer>
    </Fragment>
  );
  //#endregion
}
